import { useCallback } from 'react';
import { useDeleteSharedPreset, usePinSharedPreset, useUpdateSharedPreset } from '@/entities/shared-presets';
import { useDeleteSystemPreset, useUpdateSystemPreset } from '@/entities/system-presets';
import type { TableViewSnapshot } from './types';

/**
 * Разбор ключей серверных пресетов списка: идентификатор записи на сервере либо `null`, если ключ
 * принадлежит пресету другого вида. Формат ключа у пресетов таблицы и у дашбордов свой.
 */
type ServerPresetKeyParsers = {
  /** Ключ системного пресета; у списка без системных пресетов (дашборды) не задаётся */
  parseSystemId?: (presetId: string) => number | null;
  /** Ключ совместного пресета */
  parseSharedId: (presetId: string) => number | null;
};

/**
 * Изменения серверных пресетов — системных и совместных — по ключу пресета: пресетов таблицы
 * и пользовательских дашбордов (у дашбордов системных нет, их ключи только совместные).
 *
 * Каждое действие возвращает `false`, если ключ принадлежит личному пресету: личный пресет ведут
 * настройки пользователя, и действие над ним выполняет вызывающий. Так вид пресета по ключу
 * разбирается в одном месте, а не в каждом экшене списка пресетов.
 *
 * Разборщики держать стабильными (модульная функция или `useCallback`): от них зависят действия.
 */
export const useServerPresetMutations = ({ parseSystemId, parseSharedId }: ServerPresetKeyParsers) => {
  const { mutate: updateSystemPreset } = useUpdateSystemPreset();
  const { mutate: deleteSystemPreset } = useDeleteSystemPreset();
  const { mutate: updateSharedPreset } = useUpdateSharedPreset();
  const { mutate: pinSharedPreset } = usePinSharedPreset();
  const { mutate: deleteSharedPreset } = useDeleteSharedPreset();

  /** Переименование или запись вида; `onSuccess` — по ответу сервера */
  const updateServerPreset = useCallback(
    (presetId: string, changes: { name?: string; settings?: TableViewSnapshot }, onSuccess?: () => void) => {
      const systemId = parseSystemId?.(presetId) ?? null;
      if (systemId !== null) {
        updateSystemPreset({ id: systemId, ...changes }, { onSuccess });
        return true;
      }
      const sharedId = parseSharedId(presetId);
      if (sharedId !== null) {
        updateSharedPreset({ id: sharedId, ...changes }, { onSuccess });
        return true;
      }
      return false;
    },
    [updateSystemPreset, updateSharedPreset, parseSystemId, parseSharedId],
  );

  /** Закрепление в левом меню: у системного — у всех, у совместного — у текущего пользователя */
  const pinServerPreset = useCallback(
    (presetId: string, pinned: boolean) => {
      const systemId = parseSystemId?.(presetId) ?? null;
      if (systemId !== null) {
        updateSystemPreset({ id: systemId, isPinned: pinned });
        return true;
      }
      const sharedId = parseSharedId(presetId);
      if (sharedId !== null) {
        pinSharedPreset({ id: sharedId, isPinned: pinned });
        return true;
      }
      return false;
    },
    [updateSystemPreset, pinSharedPreset, parseSystemId, parseSharedId],
  );

  /**
   * Удаление: системного — у всех, совместного — у всех от владельца и только из своего списка
   * от получателя; `onSuccess` — по ответу сервера
   */
  const deleteServerPreset = useCallback(
    (presetId: string, onSuccess?: () => void) => {
      const systemId = parseSystemId?.(presetId) ?? null;
      if (systemId !== null) {
        deleteSystemPreset(systemId, { onSuccess });
        return true;
      }
      const sharedId = parseSharedId(presetId);
      if (sharedId !== null) {
        deleteSharedPreset(sharedId, { onSuccess });
        return true;
      }
      return false;
    },
    [deleteSystemPreset, deleteSharedPreset, parseSystemId, parseSharedId],
  );

  return { updateServerPreset, pinServerPreset, deleteServerPreset };
};
